import { type Actor, type PageRect, pageRectSchema } from './board'
import { z } from 'zod'

// Materials: PDFs in a board's library (MATERIALS.md). The original never
// changes; what the browser derived from it when it was added (page images,
// the text with where each line is) is stored next to it. Clips on the board
// point into a material by page and region.

export type MaterialPage = {
  /** Page size in PDF points. */
  w: number
  h: number
}

export type OutlineEntry = { title: string; page: number; level: number }

export type Material = {
  id: string
  name: string
  /** The original PDF among the board's files. */
  fileKey: string
  size: number
  pages: MaterialPage[]
  /** The PDF's own bookmarks, as the publisher made them; empty when it has none. */
  outline: OutlineEntry[]
  /** How the page images were encoded (browsers that cannot write WebP send JPEG). */
  pageMime: PageMime
  createdBy: Actor
  createdAt: number
}

export const PAGE_MIMES = ['image/webp', 'image/jpeg'] as const
export type PageMime = (typeof PAGE_MIMES)[number]

const outlineEntry = z.object({
  title: z.string().max(500),
  page: z.number().int().min(1),
  level: z.number().int().min(0).max(20),
})

export const MATERIAL_MAX_PAGES = 2000

/** What the browser sends to add a material, after uploading the PDF and what it derived. */
export const newMaterialSchema = z.object({
  fileKey: z.string().min(1).max(200),
  name: z.string().min(1).max(255),
  pages: z
    .array(z.object({ w: z.number().positive().max(1e5), h: z.number().positive().max(1e5) }))
    .min(1)
    .max(MATERIAL_MAX_PAGES),
  outline: z.array(outlineEntry).max(5000),
  pageMime: z.enum(PAGE_MIMES),
})
export type NewMaterial = z.infer<typeof newMaterialSchema>

/**
 * Text as a person would type it. Some PDFs write CJK characters with their
 * Kangxi radical look-alikes (⼯ for 工, ⽩ for 白), which no search matches.
 * Only those blocks are folded: full NFKC would also rewrite the original's
 * full-width punctuation.
 */
export function plainText(s: string): string {
  return s.replace(/[\u2E80-\u2EFF\u2F00-\u2FDF]/g, (c) => c.normalize('NFKC'))
}

/** A material's text with every line made plain (texts stored before plainText existed). */
export function plainMaterialText(t: MaterialText): MaterialText {
  return { pages: t.pages.map((lines) => lines.map((l) => ({ ...l, t: plainText(l.t) }))) }
}

/** One line of a page's text and where it is, as fractions of the page. */
export type TextLine = {
  t: string
  x: number
  y: number
  w: number
  h: number
  /**
   * Where each character of `t` starts, and (last) where the line ends, as
   * fractions of the page width: `t.length + 1` numbers. Absent in text stored
   * before quotes existed; positions are then spread evenly over the line.
   */
  xs?: number[] | undefined
}
/** A material's text, page by page (index 0 is page 1). */
export type MaterialText = { pages: TextLine[][] }

export const materialTextSchema = z.object({
  pages: z.array(
    z
      .array(
        z.object({
          t: z.string().max(5000),
          x: z.number(),
          y: z.number(),
          w: z.number(),
          h: z.number(),
          xs: z.array(z.number()).max(5001).optional(),
        }),
      )
      .max(5000),
  ),
})

/** Where character `i` of a line starts (i = t.length: where it ends), as a fraction of the page width. */
export function charX(l: TextLine, i: number): number {
  const at = l.xs?.[i]
  if (at !== undefined && l.xs?.length === l.t.length + 1) return at
  return l.x + (l.w * Math.min(Math.max(i, 0), l.t.length)) / Math.max(l.t.length, 1)
}

/** A run of text found on a page: from (line, char) to (line, char), end exclusive. */
export type TextSpan = { from: { line: number; char: number }; to: { line: number; char: number } }

/**
 * Where `quote` is in a page's lines, ignoring whitespace, case and line
 * breaks (a sentence wraps; PDFs space words unevenly): every match, in order.
 */
export function findQuote(lines: TextLine[], quote: string): TextSpan[] {
  const stream: { line: number; char: number }[] = []
  let hay = ''
  lines.forEach((l, li) => {
    const t = plainText(l.t)
    for (let ci = 0; ci < t.length; ci++) {
      const ch = t[ci] ?? ''
      if (/\s/.test(ch)) continue
      stream.push({ line: li, char: ci })
      hay += ch.toLowerCase()
    }
  })
  const needle = plainText(quote).replace(/\s+/g, '').toLowerCase()
  if (!needle) return []
  const out: TextSpan[] = []
  for (let at = hay.indexOf(needle); at >= 0; at = hay.indexOf(needle, at + 1)) {
    const first = stream[at]
    const last = stream[at + needle.length - 1]
    if (first && last) out.push({ from: first, to: { line: last.line, char: last.char + 1 } })
  }
  return out
}

/** The words of a span, as the page has them (line breaks as spaces). */
export function spanText(lines: TextLine[], s: TextSpan): string {
  const parts: string[] = []
  for (let li = s.from.line; li <= s.to.line; li++) {
    const t = lines[li]?.t ?? ''
    parts.push(
      t.slice(li === s.from.line ? s.from.char : 0, li === s.to.line ? s.to.char : t.length),
    )
  }
  return plainText(parts.join(' ').replace(/\s+/g, ' ').trim())
}

/** The boxes a span covers, one per line, as fractions of the page. */
export function spanRects(lines: TextLine[], s: TextSpan): PageRect[] {
  const out: PageRect[] = []
  for (let li = s.from.line; li <= s.to.line; li++) {
    const l = lines[li]
    if (!l) continue
    const x0 = charX(l, li === s.from.line ? s.from.char : 0)
    const x1 = charX(l, li === s.to.line ? s.to.char : l.t.length)
    if (x1 > x0) out.push({ x: x0, y: l.y, w: x1 - x0, h: l.h })
  }
  return out
}

/**
 * What to show around a span: the whole lines it is on, with `context` lines
 * of the same block above and below, and a little margin.
 */
export function spanViewport(lines: TextLine[], s: TextSpan, context = 1): PageRect {
  const block = (i: number) => lines[i]
  let top = s.from.line
  let bottom = s.to.line
  // Lines of the same block: close below each other and overlapping sideways.
  const near = (a: TextLine | undefined, b: TextLine | undefined) =>
    !!a &&
    !!b &&
    Math.abs(b.y - (a.y + a.h)) < a.h * 1.2 &&
    Math.min(a.x + a.w, b.x + b.w) > Math.max(a.x, b.x)
  for (let k = 0; k < context && near(block(top - 1), block(top)); k++) top--
  for (let k = 0; k < context && near(block(bottom), block(bottom + 1)); k++) bottom++
  const box = lines.slice(top, bottom + 1)
  const x0 = Math.min(...box.map((l) => l.x))
  const y0 = Math.min(...box.map((l) => l.y))
  const x1 = Math.max(...box.map((l) => l.x + l.w))
  const y1 = Math.max(...box.map((l) => l.y + l.h))
  const pad = 0.008
  const x = Math.max(0, x0 - pad)
  const y = Math.max(0, y0 - pad)
  return { x, y, w: Math.min(1, x1 + pad) - x, h: Math.min(1, y1 + pad) - y }
}

/** Long side of a stored page image, in pixels: about what a model looks at in one image. */
export const PAGE_IMAGE_LONG_SIDE = 1568

/** Pixel size of a page's stored image (and of the frame tools give coordinates in). */
export function pageImageSize(p: MaterialPage): { w: number; h: number } {
  const scale = PAGE_IMAGE_LONG_SIDE / Math.max(p.w, p.h)
  return { w: Math.round(p.w * scale), h: Math.round(p.h * scale) }
}

/** Height of a clip's caption row on the board (its source and page). */
export const CLIP_CAPTION_H = 30

/**
 * A clip's size on the board: three quarters of its width in page pixels (a
 * cut the width of the page would be ~900 wide, capped at 640), at least 300
 * so a small figure can still be read, the region's shape kept.
 */
export function clipSize(p: MaterialPage, r: PageRect, width?: number): { w: number; h: number } {
  const frame = pageImageSize(p)
  const w = Math.round(width ?? Math.min(640, Math.max(300, r.w * frame.w * 0.75)))
  const aspect = (r.h * frame.h) / (r.w * frame.w)
  return { w, h: Math.round(w * aspect) + CLIP_CAPTION_H }
}

/** R2 keys of what is derived from a material's PDF, under the board's prefix. */
export const materialKeys = {
  page: (fileKey: string, page: number) => `${fileKey}.pages/${page}`,
  text: (fileKey: string) => `${fileKey}.text.json`,
}

/** The text of the lines that fall mostly inside `r`, in reading order. */
export function textIn(lines: TextLine[], r: PageRect): string {
  const inside = lines.filter((l) => {
    const ix = Math.min(l.x + l.w, r.x + r.w) - Math.max(l.x, r.x)
    const iy = Math.min(l.y + l.h, r.y + r.h) - Math.max(l.y, r.y)
    return ix > 0 && iy > 0 && (ix * iy) / Math.max(l.w * l.h, 1e-9) > 0.5
  })
  return inside.map((l) => l.t).join('\n')
}

// ---- links into a material: mat://ID/PAGE or mat://ID/PAGE#x,y,w,h ----

export type MaterialRef = { materialId: string; page: number; rect: PageRect | null }

const MAT_HREF = /^mat:\/\/([\w-]{1,64})\/(\d{1,6})(?:#([\d.]+),([\d.]+),([\d.]+),([\d.]+))?$/

/** A `mat://` href, or null. The region is in fractions of the page. */
export function parseMaterialHref(href: string): MaterialRef | null {
  const m = MAT_HREF.exec(href)
  if (!m) return null
  const page = Number(m[2])
  if (!m[3]) return { materialId: m[1] ?? '', page, rect: null }
  const rect = pageRectSchema.safeParse({
    x: Number(m[3]),
    y: Number(m[4]),
    w: Number(m[5]),
    h: Number(m[6]),
  })
  return rect.success ? { materialId: m[1] ?? '', page, rect: rect.data } : null
}

export function materialHref(ref: MaterialRef): string {
  const r = ref.rect
  const f = (n: number) => String(Math.round(n * 10000) / 10000)
  return `mat://${ref.materialId}/${ref.page}${r ? `#${f(r.x)},${f(r.y)},${f(r.w)},${f(r.h)}` : ''}`
}
